import { Box, HBox, VBox } from "@ui/box/Box"
import Button from "@ui/button/Button"
import { Typo } from "@ui/text/Typo"
import type { RecursiveCSS } from "@ui/theme/GlobalTheme"
import type { ReactNode } from "react"
import { LogoMark } from "../assets/Logo"
import { DS } from "../ds/Kit"
import { PRIVATE_MOBILE_BREAKPOINT as MOBILE, PRIVATE_THEME as T } from "./theme"

export const BrandLockup = ({ light = false, compact = false }: { light?: boolean; compact?: boolean }) => (
	<HBox gap="11px" sx={{ alignItems: "center" }}>
		<Box sx={{ flexShrink: 0, overflow: "hidden", borderRadius: compact ? "10px" : "12px", boxShadow: light ? "0 9px 22px rgba(2, 13, 24, .28)" : DS.shadow }}>
			<LogoMark size={compact ? "34px" : "40px"} />
		</Box>
		<VBox gap="1px">
			<Typo sx={{ color: light ? "#fff" : T.text, fontWeight: 700, fontSize: compact ? "1.02rem" : "1.14rem", letterSpacing: "-0.035em", lineHeight: 1.1 }}>
				Cardpartner
			</Typo>
			<Typo sx={{ color: light ? T.sidebarMuted : T.muted, fontWeight: 600, fontSize: "0.75rem", lineHeight: 1.2 }}>
				Private
			</Typo>
		</VBox>
	</HBox>
)

/** Page title block. The eyebrow prop is kept for call-site compatibility but is no longer rendered. */
export const PageIntro = ({
	title,
	description,
	action
}: {
	eyebrow?: string
	title: string
	description: ReactNode
	action?: ReactNode
}) => (
	<HBox sx={{ justifyContent: "space-between", alignItems: "flex-end", gap: "20px", flexWrap: "wrap", [MOBILE]: { alignItems: "flex-start", gap: "12px", padding: "0 2px" } }}>
		<VBox gap="4px" sx={{ minWidth: 0 }}>
			<Typo sx={{ color: DS.ink, fontSize: "clamp(22px, 2vw, 26px)", lineHeight: 1.15, fontWeight: 700, letterSpacing: "-0.03em" }}>{title}</Typo>
			<Typo sx={{ color: DS.muted, fontSize: "14px", lineHeight: 1.5, maxWidth: "700px" }}>{description}</Typo>
		</VBox>
		{action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
	</HBox>
)

export const PrivateSurface = ({ children, sx }: { children: ReactNode; sx?: RecursiveCSS }) => (
	<Box
		sx={{
			background: DS.surface,
			minWidth: 0,
			border: `1px solid ${DS.line}`,
			boxShadow: DS.shadow,
			...sx,
			borderRadius: DS.radius
		}}>
		{children}
	</Box>
)

/** Small sentence-case pill used for section captions. */
export const SectionLabel = ({ children, tone = "accent" }: { children: ReactNode; tone?: "accent" | "neutral" | "warning" }) => {
	const color = tone === "warning" ? "#92400E" : tone === "neutral" ? DS.muted : DS.accentInk
	const backgroundColor = tone === "warning" ? "#FFF6DE" : tone === "neutral" ? DS.soft : DS.accentSoft
	return (
		<Box sx={{ display: "inline-flex", alignItems: "center", alignSelf: "flex-start", padding: "4px 10px", borderRadius: "999px", color, backgroundColor, fontSize: "12px", fontWeight: 600 }}>
			{children}
		</Box>
	)
}

export const EmptyState = ({ icon, title, description, action }: { icon: ReactNode; title: string; description: string; action?: ReactNode }) => (
	<VBox center gap="10px" sx={{ padding: "48px 22px", textAlign: "center" }}>
		<Box aria-hidden sx={{ width: "52px", height: "52px", borderRadius: "16px", display: "flex", alignItems: "center", justifyContent: "center", color: DS.accent, background: DS.accentSoft, marginBottom: "4px" }}>{icon}</Box>
		<Typo sx={{ color: DS.ink, fontWeight: 650, fontSize: "16px" }}>{title}</Typo>
		<Typo sx={{ color: DS.muted, maxWidth: "380px", fontSize: "14px", lineHeight: 1.5 }}>{description}</Typo>
		{action && <Box sx={{ marginTop: "8px" }}>{action}</Box>}
	</VBox>
)

export const IDENTITY_REQUIRED_MESSAGE = "Complete identity verification to unlock account activity and card features."

/** Inline status callout in the Kit palette: warning, error, info or success. */
export const Callout = ({
	tone,
	title,
	children,
	action,
	icon,
	role,
	ariaLabel
}: {
	tone: "warning" | "error" | "info" | "success"
	title: string
	children?: ReactNode
	action?: ReactNode
	icon?: ReactNode
	role?: "alert" | "status"
	ariaLabel?: string
}) => {
	const palette = {
		warning: { bg: "#FFFBEB", border: "#FDE68A", ink: "#92400E", tile: "#FEF3C7" },
		error: { bg: "#FEF2F2", border: "#FECACA", ink: "#991B1B", tile: "#FEE2E2" },
		info: { bg: DS.accentSoft, border: "#D9DCFF", ink: DS.accentInk, tile: "#E0E3FF" },
		success: { bg: "#ECFDF5", border: "#A7F3D0", ink: "#065F46", tile: "#D1FAE5" }
	}[tone]
	return (
		<HBox
			role={role}
			aria-label={ariaLabel}
			sx={{ justifyContent: "space-between", alignItems: "center", gap: "16px", padding: "14px 16px", borderRadius: DS.radius, backgroundColor: palette.bg, border: `1px solid ${palette.border}`, flexWrap: "wrap" }}>
			<HBox gap="12px" sx={{ alignItems: "center", minWidth: 0, flex: "1 1 260px" }}>
				<Box aria-hidden sx={{ width: "38px", height: "38px", flexShrink: 0, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", color: palette.ink, backgroundColor: palette.tile }}>{icon ?? (tone === "success" ? <IconCheck /> : tone === "info" ? <IconInfo /> : <IconNotice />)}</Box>
				<VBox gap="2px" sx={{ minWidth: 0 }}>
					<Typo sx={{ color: palette.ink, fontSize: "14px", fontWeight: 650 }}>{title}</Typo>
					{children && <Typo sx={{ color: DS.text, fontSize: "13px", lineHeight: 1.45 }}>{children}</Typo>}
				</VBox>
			</HBox>
			{action}
		</HBox>
	)
}

export const PrivateRequestError = ({ message, onRetry }: { message: string; onRetry: () => void }) => {
	const verificationRequired = message === IDENTITY_REQUIRED_MESSAGE
	return (
		<Callout
			role="alert"
			tone={verificationRequired ? "warning" : "error"}
			icon={verificationRequired ? <IconShield /> : undefined}
			title={verificationRequired ? "Verification required" : "Something went wrong"}
			action={<Button variant="secondary" onClick={onRetry} sx={{ minHeight: "40px", padding: "0 16px", borderRadius: "12px" }}>{verificationRequired ? "Refresh status" : "Try again"}</Button>}
		>
			{message}
		</Callout>
	)
}

export const IconShield = () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 3.5 19 6v5.2c0 4.2-2.8 7.7-7 9.3-4.2-1.6-7-5.1-7-9.3V6l7-2.5Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="m8.8 12 2.1 2.1 4.5-4.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
export const IconNotice = () => <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 8v5M12 16.5h.01" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" /><path d="M10.2 4.7 3.5 17a2 2 0 0 0 1.8 3h13.4a2 2 0 0 0 1.8-3L13.8 4.7a2 2 0 0 0-3.6 0Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>
const IconInfo = () => <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" /><path d="M12 11v5M12 8h.01" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" /></svg>
const IconCheck = () => <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="m5 12.5 4.2 4.2L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
